import { FiExternalLink, FiX } from 'react-icons/fi';
import CustomModal from './atoms/CustomModal';
import { HELP_GUIDE_SECTIONS, HelpGuideSection } from '../utils/helpGuides';

// Relative on purpose. `/productores/:path*` is rewritten to the landing site
// in next.config.mjs, so this resolves to the guides on whatever host we are
// running on. The guides answer with `X-Frame-Options: SAMEORIGIN`, and going
// through the rewrite is what keeps the iframe same-origin — pointing straight
// at avantti.com.ar gets the frame refused everywhere but production.
const GUIDES_URL = '/productores/guias';
const GUIDES_TITLE = 'Guías de la sección';

interface HelpGuideModalProps {
  section: HelpGuideSection;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Shows the published guides site as-is, inside an iframe — the content is not
 * duplicated here, so editing that site is enough to update the help.
 *
 * The iframe lands on the section index, which lists every guide for it; the
 * new-tab link is the same page without `?embed=1`, so it opens with the site's
 * header, the way a normal visitor sees it.
 */
const HelpGuideModal = ({ section, isOpen, onClose }: HelpGuideModalProps) => {
  const url = `${GUIDES_URL}/${section}`;

  return (
    <CustomModal contentLabel={GUIDES_TITLE} isOpen={isOpen} onRequestClose={onClose} card size="wide">
      <div className="flex flex-col min-h-0 flex-1">
        <div className="flex flex-row items-center gap-3 px-5 py-3 border-b border-border-secondary">
          <h2 className="flex-1 min-w-0 truncate text-lg font-medium text-text-primary">{GUIDES_TITLE}</h2>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title="Abrir en una pestaña nueva"
            aria-label="Abrir las guías en una pestaña nueva"
            className="flex-shrink-0 text-text-secondary hover:text-primary-500 transition-colors"
          >
            <FiExternalLink className="w-5 h-5" />
          </a>
          <button
            type="button"
            onClick={onClose}
            title="Cerrar"
            aria-label="Cerrar las guías"
            className="flex-shrink-0 text-text-secondary hover:text-primary-500 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <iframe
          src={`${url}?embed=1`}
          title={`Guías: ${HELP_GUIDE_SECTIONS[section]}`}
          className="w-full h-[70svh] md:h-[75vh] border-0 bg-surface-base"
        />
      </div>
    </CustomModal>
  );
};

export default HelpGuideModal;
